import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { Button, Field, Icon, Overlay, TextInput } from "../../ui";
import { useBreakpoint } from "../../lib/useBreakpoint";
import styles from "./ConfirmDialog.module.css";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  phrase: string;
  phraseLabel: string;
  placeholder?: string;
  mismatchHint: string;
  children: ReactNode;
  busy?: boolean;
  error?: string;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  phrase,
  phraseLabel,
  placeholder,
  mismatchHint,
  children,
  busy = false,
  error,
  onConfirm,
  onClose,
}: ConfirmDialogProps): ReactElement {
  const { isMobile } = useBreakpoint();
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const matches = typed === phrase;
  const showMismatch = typed.length > 0 && !matches;

  return (
    <Overlay
      open={open}
      onClose={onClose}
      placement={isMobile ? "sheet-bottom" : "center"}
      label={title}
      panelStyle={
        isMobile
          ? undefined
          : // Centre on the content column, not the window.
            {
              inlineSize: "min(88vw, 560px)",
              translate: "calc(var(--nav-w) / 2) 0",
            }
      }
    >
      <div className={styles.dialog}>
        <div className={styles.heading}>
          <span className={styles.warning} aria-hidden>
            <Icon name="alert" size={20} />
          </span>
          <h2 className={styles.title}>{title}</h2>
        </div>
        <div className={styles.body}>{children}</div>
        <Field
          label={phraseLabel}
          error={showMismatch ? mismatchHint : undefined}
        >
          <TextInput
            value={typed}
            placeholder={placeholder}
            autoComplete="off"
            invalid={showMismatch}
            onChange={(event) => setTyped(event.target.value)}
          />
        </Field>
        {error ? <p className={styles.error}>{error}</p> : null}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!matches}
            loading={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Overlay>
  );
}
